import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { Candidate, CandidateFilter, MunicipalCandidate, TabulationPermission, User, VoterGroupsFilters } from '../../../types';
import React, { useEffect, useState } from 'react';
import {
  fetchBarangaysByMunicipality,
  fetchMunicipalitiesByDistrict,
  fetchPrecinctsByBarangay
} from '../../../utils/locationUtils';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchAllCandidates } from '../../../utils/candidates';
import useAuth from '../../../hooks/useAuth';

interface VoterGroupsFormProps {
  onGenerateReport: (filters: VoterGroupsFilters) => void;
}

const VoterGroupsForm: React.FC<VoterGroupsFormProps> = ({ onGenerateReport }) => {
  const { user } = useAuth();
  
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [barangays, setBarangays] = useState<string[]>([]);
  const [precincts, setPrecincts] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  const [filters, setFilters] = useState<VoterGroupsFilters>({
    region: 'REGION XIII',
    province: 'SURIGAO DEL NORTE',
    district: 'DISTRICT 1',
    municipality: '',
    barangay: '',
    precinct: ''
  });

  useEffect(() => {
    const loadInitialData = async (user: User) => {
      try {
        const loadedCandidates = await fetchAllCandidates();
        setCandidates(loadedCandidates);

        const districtMunicipalities = await fetchMunicipalitiesByDistrict(
          filters.region, 
          filters.province, 
          filters.district
        );

        // Filter municipalities based on user's allowedTabulations if set
        const filteredMunicipalities = user.allowedTabulations 
          ? districtMunicipalities.filter(municipality => 
              user.allowedTabulations?.some(
                (permission: TabulationPermission) => 
                  permission.type === 'municipal' && permission.id === municipality
              )
            )
          : districtMunicipalities;

        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    if (user) {
      loadInitialData(user);
    }
  }, [user]);

  const handleMunicipalityChange = async (municipality: string) => {
    try {
      const newBarangays = await fetchBarangaysByMunicipality(
        filters.region, 
        filters.province, 
        filters.district, 
        municipality
      );
      setBarangays(newBarangays);
      setFilters(prev => ({
        ...prev,
        municipality,
        barangay: '',
        precinct: '',
        candidateFilters: [{
          position: '',
          candidate: '',
          matchType: 'match'
        }]
      }));
      // Reset subsequent dropdowns
      setPrecincts([]);
    } catch (error) {
      console.error('Error fetching barangays:', error);
    }
  };

  const handleBarangayChange = async (barangay: string) => {
    try {
      const newPrecincts = await fetchPrecinctsByBarangay(
        filters.region, 
        filters.province, 
        filters.district, 
        filters.municipality, 
        barangay
      );
      setPrecincts(newPrecincts);
      setFilters(prev => ({
        ...prev,
        barangay,
        precinct: ''
      }));
    } catch (error) {
      console.error('Error fetching precincts:', error);
    }
  };

  const handleSubmit = () => {
    // Validate mandatory fields
    if (!filters.region || !filters.province || !filters.district || 
        !filters.municipality || !filters.barangay) {
      alert('Please fill in all required fields');
      return;
    }

    onGenerateReport(filters);
  };

  // Get unique positions from candidates
  const availablePositions = Array.from(new Set(
    candidates.map(candidate => candidate.position)
  ));

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {/* Municipality Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Municipality *</InputLabel>
          <Select
            value={filters.municipality}
            label="Municipality *"
            onChange={(e) => handleMunicipalityChange(e.target.value)}
            disabled={!filters.district}
            required
          >
            {municipalities.map(municipality => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Barangay Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Barangay *</InputLabel>
          <Select
            value={filters.barangay}
            label="Barangay *"
            onChange={(e) => handleBarangayChange(e.target.value)}
            disabled={!filters.municipality}
            required
          >
            {barangays.map(barangay => (
              <MenuItem key={barangay} value={barangay}>
                {barangay}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Optional Precinct Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Precinct (Optional)</InputLabel>
          <Select
            value={filters.precinct || ''}
            label="Precinct (Optional)"
            onChange={(e) => setFilters(prev => ({
              ...prev,
              precinct: e.target.value
            }))}
            disabled={!filters.barangay}
          >
            <MenuItem value="">All Precincts</MenuItem>
            {precincts.map(precinct => (
              <MenuItem key={precinct} value={precinct}>
                {precinct}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Submit Button */}
      <Grid item xs={12}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={!filters.region || !filters.province || 
                    !filters.district || !filters.municipality || 
                    !filters.barangay}
        >
          Generate Report
        </Button>
      </Grid>
    </Grid>
  );
};

export default VoterGroupsForm;
